import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Battery, Gauge, Ruler, Weight, Package, Home as HomeIcon, ArrowRight, CheckCircle2, Info } from "lucide-react";
import { useLang, localized } from "../i18n/LanguageContext";
import { fromPerDay } from "../lib/pricing";
import useSEO from "../hooks/useSEO";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const Spec = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-100">
    <Icon className="w-5 h-5 text-brand mt-1 shrink-0" />
    <div><div className="text-sm text-ink-muted">{label}</div><div className="text-lg font-semibold text-ink">{value}</div></div>
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const { lang, tr, lp } = useLang();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [slug]);
  useSEO({
    title: p ? `${localized(p.name, lang)} — MobiRider` : "MobiRider",
    description: p ? localized(p.short, lang) : "",
    lang,
    jsonLd: p ? { "@context": "https://schema.org", "@type": "Product", name: localized(p.name, lang), description: localized(p.description, lang), image: p.image, offers: { "@type": "Offer", price: p.pricing?.day1, priceCurrency: "EUR" } } : null,
  });

  if (p === false) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-xl">404</div>;
  if (!p) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-xl">{tr.common.loading}</div>;

  const s = p.specs || {};
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="rounded-3xl overflow-hidden bg-slate-50 border border-slate-100">
          <img src={p.image} alt={localized(p.name, lang)} className="w-full h-full object-cover aspect-square" />
        </div>
        <div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-ink tracking-tight">{localized(p.name, lang)}</h1>
          <p className="text-xl text-ink-muted mt-4 leading-relaxed">{localized(p.description, lang)}</p>
          <div className="mt-6 flex items-end gap-2">
            <span className="text-ink-muted text-lg">{tr.booking.from}</span>
            <span className="font-heading font-extrabold text-4xl text-brand">{fromPerDay(p)} €</span>
            <span className="text-ink-muted text-lg">{tr.products.perDay}</span>
          </div>
          <div className="text-sm text-ink-muted mt-1">{tr.products.from7}</div>
          <div className="mt-4 bg-cream rounded-2xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-brand mt-0.5" />
            <div className="text-base text-ink-muted"><b className="text-ink">{tr.products.deposit}:</b> {p.deposit?.toFixed(0)} € — {tr.products.recommendations}: {localized(p.recommendations, lang)}</div>
          </div>
          <Link to={`${lp("/reservation")}?product=${p.id}`}>
            <Button data-testid="detail-book-btn" className="mt-6 w-full h-16 rounded-full bg-accent2 hover:bg-accent2-hover text-white font-bold text-xl">{tr.products.book} <ArrowRight className="w-6 h-6 ml-2" /></Button>
          </Link>

          <h2 className="font-heading font-bold text-2xl text-ink mt-10 mb-2">{tr.products.specs}</h2>
          <div className="grid sm:grid-cols-2 gap-x-8">
            {s.autonomy_km > 0 && <Spec icon={Battery} label={tr.products.autonomy} value={`${s.autonomy_km} km`} />}
            <Spec icon={Gauge} label={tr.products.maxWeight} value={`${s.max_user_weight_kg} kg`} />
            <Spec icon={Ruler} label={tr.products.dimensions} value={`${s.dimensions_cm} cm`} />
            <Spec icon={Ruler} label={tr.products.width} value={`${s.width_cm} cm`} />
            <Spec icon={Weight} label={tr.products.weight} value={`${s.device_weight_kg} kg`} />
            <Spec icon={Package} label={tr.products.foldable} value={s.foldable ? tr.products.yes : tr.products.no} />
            <Spec icon={HomeIcon} label={tr.products.usage} value={tr.products.indoorOutdoor} />
            <Spec icon={CheckCircle2} label={tr.products.transport} value={localized(s.transport, lang)} />
            {s.max_speed_kmh > 0 && <Spec icon={Gauge} label={tr.products.maxSpeed} value={`${s.max_speed_kmh} km/h`} />}
            {s.turning_radius_cm > 0 && <Spec icon={Ruler} label={tr.products.turningRadius} value={`${s.turning_radius_cm} cm`} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
